// A scan's camera images (prep/images.py) in the editor: each frame's optical camera, its frustum, and its
// photo + depth map, loaded on request for Viewer.setImage.
import type { OrigImages } from '../format.ts';
import * as cache from '../cache.ts';
import type { V3 } from '../math.ts';
import type { Rigid } from '../align.ts';

export interface CamImage { K: number[]; pose: Rigid }
/** url: the dir holding <frame>.jpg / <frame>.d16 (ending in '/'). Poses in the scan's file frame. */
/** src: the manifest it was read from (hashed: new photos, a new name). */
export interface CamImages { url: string; src?: string; w: number; h: number; dw: number; dh: number; cams: Map<number, CamImage> }

const rigid12 = (p: number[]): Rigid => ({ R: [p[0], p[1], p[2], p[4], p[5], p[6], p[8], p[9], p[10]], t: [p[3], p[7], p[11]] });

export function imagesOf(doc: OrigImages, base: string, src?: string): CamImages {
  const cams = new Map<number, CamImage>();
  doc.frames.forEach((f, i) => cams.set(f, { K: doc.K[i], pose: rigid12(doc.pose[i]) }));
  return { url: base + doc.dir + '/', src, w: doc.w, h: doc.h, dw: doc.dw, dh: doc.dh, cams };
}

/**
 * An optical camera's frustum (x right, y down, z forward) out to depth d, from its depth grid's intrinsics:
 * the 4 rays to the image corners, the far rectangle, and a tick up (-y) from the far top edge.
 */
export function opticalFrustum(K: number[], dw: number, dh: number, d = 0.3): [V3, V3][] {
  const [fx, fy, cx, cy] = K, x0 = (-0.5 - cx) / fx * d, x1 = (dw - 0.5 - cx) / fx * d;
  const y0 = (-0.5 - cy) / fy * d, y1 = (dh - 0.5 - cy) / fy * d;
  const far: V3[] = [[x0, y0, d], [x1, y0, d], [x1, y1, d], [x0, y1, d]];
  return [...far.map((p): [V3, V3] => [[0, 0, 0], p]), ...far.map((p, i): [V3, V3] => [p, far[(i + 1) % 4]]),
    [[(x0 + x1) / 2, y0, d], [(x0 + x1) / 2, y0 - 0.1, d]]];
}

/** RTAB-Map's base frame camera (x forward, z up) as a small frustum along x, with a tick up from its far edge. */
export function baseFrustum(d = 0.3, w = 0.2, h = 0.15): [V3, V3][] {
  const far: V3[] = [[d, w, h], [d, -w, h], [d, -w, -h], [d, w, -h]];
  return [...far.map((p): [V3, V3] => [[0, 0, 0], p]), ...far.map((p, i): [V3, V3] => [p, far[(i + 1) % 4]]), [[d, 0, h], [d, 0, h + 0.1]]];
}

/** A .d16 depth map (little-endian uint16 mm) in metres. */
export function decodeDepth(buf: ArrayBuffer): Float32Array {
  const dv = new DataView(buf), n = buf.byteLength >> 1, z = new Float32Array(n);
  for (let i = 0; i < n; i++) z[i] = dv.getUint16(2 * i, true) / 1000;
  return z;
}

/** Frame f's photo and depth (metres). */
export async function loadImage(ci: CamImages, f: number): Promise<{ img: ImageBitmap; depth: Float32Array }> {
  // hashed dir (<id>.<hash>.img/): kept in the browser
  const [jpg, d16] = await Promise.all([cache.fetchBuf(ci.url + `${f}.jpg`).then(b => new Blob([b], { type: 'image/jpeg' })), cache.fetchBuf(ci.url + `${f}.d16`)]);
  const depth = decodeDepth(d16);
  if (depth.length !== ci.dw * ci.dh) throw new Error(`${f}.d16: ${depth.length} pixels, not ${ci.dw} x ${ci.dh}`);
  return { img: await createImageBitmap(jpg), depth };
}

/** Frame f's depth (metres) alone. */
export async function loadDepth(ci: CamImages, f: number): Promise<Float32Array> {
  const depth = decodeDepth(await cache.fetchBuf(ci.url + `${f}.d16`));
  if (depth.length !== ci.dw * ci.dh) throw new Error(`${f}.d16: ${depth.length} pixels, not ${ci.dw} x ${ci.dh}`);
  return depth;
}
